import { Stack } from '@mui/material';
import { differenceInDays, isAfter, parse, parseISO } from 'date-fns';
import { useState } from 'react';
import DatePicker from '../../../../../../components/FormInputs/DatePicker';
import LangEnum from '../../../../../../lang/LangEnum';
import { GeneralSettingsParams } from '../../../../../../stores/GeneralSettings/generalSettings.types';
import snackbarStore from '../../../../../../stores/Snackbar.store';
import teceoDatePickerConfig from '../../../../../../utils/TeceoDatePickerConfig';
import { formatDateFromApi, formatDateToApi, isDateValid } from '../../../../../../utils/dates';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { handleChangeAndBlurParams } from '../../../../types/generalSettingsPage.types';

interface SelloutDateRangeInputProps {
  selloutPeriodCustomIntervalStartDate?: string;
  selloutPeriodCustomIntervalEndDate?: string;

  handleChangeAndBlurList: (values: handleChangeAndBlurParams[]) => Promise<void>;
}

const parseDateStringOrNull = (dateString?: string): Date | null => {
  if (!dateString) {
    return null;
  }

  return parseISO(formatDateFromApi(dateString)!);
};

export const SelloutDateRangeInput = ({
  selloutPeriodCustomIntervalStartDate,
  selloutPeriodCustomIntervalEndDate,
  handleChangeAndBlurList,
}: SelloutDateRangeInputProps) => {
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;

  const [startDate, setStartDate] = useState<Date | null>(
    parseDateStringOrNull(selloutPeriodCustomIntervalStartDate),
  );
  const [endDate, setEndDate] = useState<Date | null>(
    parseDateStringOrNull(selloutPeriodCustomIntervalEndDate),
  );

  const validateDateRange = (
    startDate: Date | null,
    endDate: Date | null,
  ): { isValid: boolean; error?: string } => {
    if (!startDate || !endDate) {
      return { isValid: true };
    }

    if (isAfter(startDate, endDate)) {
      return {
        isValid: false,
        error: translate(LangEnum.GLOBAL_END_DATE_ERROR),
      };
    }

    if (differenceInDays(endDate, startDate) > 365) {
      return {
        isValid: false,
        error: translate(LangEnum.GENERAL_SETTINGS_CUSTOM_INTERVAL_MAX_ERROR),
      };
    }

    return { isValid: true };
  };

  const dateError = validateDateRange(startDate, endDate).error;

  const handleDateChange = (date: Date | null, name: keyof GeneralSettingsParams) => {
    if (name === 'selloutPeriodCustomIntervalStartDate') {
      setStartDate(date);
    } else if (name === 'selloutPeriodCustomIntervalEndDate') {
      setEndDate(date);
    }

    const newStartDate = name === 'selloutPeriodCustomIntervalStartDate' ? date : startDate;
    const newEndDate = name === 'selloutPeriodCustomIntervalEndDate' ? date : endDate;

    const dateValidation = validateDateRange(newStartDate, newEndDate);

    if (!dateValidation.isValid) {
      handleMessage({
        type: 'error',
        message: dateValidation.error ?? translate(LangEnum.GLOBAL_ERROR),
      });
      return;
    }

    handleChangeAndBlurList([
      {
        name: 'selloutPeriodCustomIntervalStartDate',
        value: newStartDate && isDateValid(newStartDate) ? formatDateToApi(newStartDate) : null,
      },
      {
        name: 'selloutPeriodCustomIntervalEndDate',
        value: newEndDate && isDateValid(newEndDate) ? formatDateToApi(newEndDate) : null,
      },
    ]);
  };

  return (
    <Stack direction="row" sx={{ gap: 2 }}>
      <DatePicker
        {...teceoDatePickerConfig}
        label={translate(LangEnum.GLOBAL_INITIAL_DATE)}
        name="selloutPeriodCustomIntervalStartDate"
        onAccept={(data) => {
          handleDateChange(data, 'selloutPeriodCustomIntervalStartDate');
        }}
        onBlur={(data) => {
          const formattedDate = parse(data.target.value, 'dd/MM/yyyy', new Date());

          handleDateChange(formattedDate, 'selloutPeriodCustomIntervalStartDate');
        }}
        value={startDate}
      />
      <DatePicker
        {...teceoDatePickerConfig}
        label={translate(LangEnum.GLOBAL_FINAL_DATE)}
        name="selloutPeriodCustomIntervalEndDate"
        onAccept={(data) => {
          handleDateChange(data, 'selloutPeriodCustomIntervalEndDate');
        }}
        onBlur={(data) => {
          const formattedDate = parse(data.target.value, 'dd/MM/yyyy', new Date());

          handleDateChange(formattedDate, 'selloutPeriodCustomIntervalEndDate');
        }}
        value={endDate}
        error={!!dateError}
        helperText={dateError}
      />
    </Stack>
  );
};
